import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email.trim(), password, remember);
    setBusy(false);
    if (!r.ok) {
      toast.error(r.error || "Échec de la connexion");
      return;
    }
    toast.success("Connecté");
    nav(r.ok ? "/" : "/login");
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div className="relative hidden md:block overflow-hidden border-r-2 border-border">
        <img
          src="https://images.unsplash.com/photo-1694113372786-2553caec0c76?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Camion sur la route"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div className="self-start inline-block bg-white pt-4 pb-4 px-6 border-2 border-white/90 shadow-[6px_6px_0_0_rgba(255,69,0,0.9)]">
            <img src="/brand/logo-mark.png" alt="TransPrestige" className="h-32 w-auto block" />
          </div>
          <div>
            <div className="label-caps text-primary mb-3">Chauffeurs · Flotte · Logistique</div>
            <h1 className="font-display font-black text-5xl leading-none tracking-tight">
              Du point A<br/>au point B.<br/>
              <span className="text-primary">Notre engagement.</span>
            </h1>
            <p className="mt-6 text-white/80 max-w-md">
              Remplissez vos bons de transport en quelques minutes. Photos, PDF, envoi courriel — tout au bout des doigts.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6">
          <div>
            <div className="label-caps text-muted-foreground mb-2">Connexion</div>
            <h2 className="font-display font-black text-4xl leading-none tracking-tight">Bienvenue.</h2>
            <p className="text-muted-foreground mt-2 text-sm">Entrez vos identifiants pour continuer.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="label-caps">Courriel</Label>
            <Input
              id="email"
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@entreprise.com"
              className="h-12 border-2 rounded-none focus-visible:ring-primary"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="label-caps">Mot de passe</Label>
            <Input
              id="password"
              data-testid="login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-12 border-2 rounded-none focus-visible:ring-primary"
            />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="remember"
              data-testid="login-remember"
              checked={remember}
              onCheckedChange={(v) => setRemember(!!v)}
            />
            <Label htmlFor="remember" className="cursor-pointer text-sm font-medium">
              Rester connecté 30 jours
            </Label>
          </div>

          <Button
            data-testid="login-submit"
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-none bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-base btn-hard active:scale-95"
          >
            {busy ? "Connexion…" : (<>Se connecter <ArrowRight className="w-4 h-4 ml-2" /></>)}
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            L'inscription est gérée par votre administrateur.
          </p>
        </form>
      </div>
    </div>
  );
}
